import React, { useState } from 'react';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  ChartData,
  ChartOptions
} from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import { Maximize2 } from 'lucide-react';
import { RestaurantPieSlice } from '../../utils/allRestaurantsDashboardCalculations';
import { TabbedSections } from '../ui/TabbedSections';
import { CardWithHeader } from '../ui/CardWithHeader';
import { ExpandModal } from '../ui/ExpandModal';
import { NoDataInPeriod } from '../ui/NoDataInPeriod';
import { cn } from '../../lib/utils';
import { tertiaryText } from '../../styling/stylingHelper';
import { getRestaurantColor } from '../../utils/restaurantColors';

ChartJS.register(ArcElement, Tooltip, Legend);

type PieMode = 'revenue' | 'costs';

interface RestaurantRevenuePieChartProps {
  revenueData: RestaurantPieSlice[];
  costData: RestaurantPieSlice[];
}

export const RestaurantRevenuePieChart: React.FC<RestaurantRevenuePieChartProps> = ({ revenueData, costData }) => {
  const { t } = useTranslation();
  const [mode, setMode] = useState<PieMode>('revenue');
  const [maximized, setMaximized] = useState(false);

  const data = mode === 'revenue' ? revenueData : costData;
  const title = mode === 'revenue' ? t('dashboard.revenueSplit') : t('dashboard.costsSplit');

  const toggleOptions = [
    { value: 'revenue', label: t('dashboard.revenue') },
    { value: 'costs', label: t('dashboard.costs') },
  ];

  const colors = data.map((d) => getRestaurantColor(d.restaurantId));

  const chartData: ChartData<'doughnut'> = {
    labels: data.map(d => d.restaurantName),
    datasets: [
      {
        data: data.map(d => d.percentage),
        backgroundColor: colors,
        borderWidth: 0,
        borderRadius: 10,
        spacing: 6,
        hoverOffset: 2,
      },
    ],
  };

  const options: ChartOptions<'doughnut'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
        external: (context) => {
          let tooltipEl = document.getElementById('restaurant-revenue-chartjs-tooltip');

          if (!tooltipEl) {
            tooltipEl = document.createElement('div');
            tooltipEl.id = 'restaurant-revenue-chartjs-tooltip';
            tooltipEl.innerHTML = `
              <div class="bg-white dark:bg-card-dark border border-[#F5F5F5] dark:border-borderGrey-dark rounded-lg p-2 shadow-lg">
                <span class="tooltip-text"></span>
              </div>
            `;
            document.body.appendChild(tooltipEl);
          }

          const tooltipModel = context.tooltip;
          if (tooltipModel.opacity === 0) {
            tooltipEl.style.opacity = '0';
            return;
          }

          if (tooltipModel.body) {
            const textSpan = tooltipEl.querySelector('.tooltip-text') as HTMLElement;
            const dataIndex = tooltipModel.dataPoints[0].dataIndex;
            const item = data[dataIndex];
            textSpan.innerHTML = `${item.restaurantName} ${item.percentage.toFixed(1)}%`;
            textSpan.style.color = colors[dataIndex];
          }

          const position = context.chart.canvas.getBoundingClientRect();
          tooltipEl.style.opacity = '1';
          tooltipEl.style.position = 'absolute';
          tooltipEl.style.left = position.left + window.pageXOffset + tooltipModel.caretX - 30 + 'px';
          tooltipEl.style.top = position.top + window.pageYOffset + tooltipModel.caretY - 40 + 'px';
          tooltipEl.style.pointerEvents = 'none';
          tooltipEl.style.zIndex = '100';
          tooltipEl.style.transition = 'all 0.1s ease';
          tooltipEl.style.transform = 'translateX(-50%)';
        },
      },
    },
  };

  const toggle = (
    <TabbedSections
      options={toggleOptions}
      value={mode}
      onChange={(v) => setMode(v as PieMode)}
      isTimeOptionSelection={true}
    />
  );

  // Chart + legend body, reused inline and (larger) inside the maximize modal.
  // The legend is capped in height and scrolls — with 40+ restaurants it would otherwise
  // make the card grow unbounded.
  const renderBody = (heightClass: string, legendMaxWidthClass: string, legendMaxHeightClass: string) => {
    if (data.length === 0) {
      return <NoDataInPeriod />;
    }
    return (
      <div className="w-full">
        <div className={cn('relative w-full flex items-center justify-center select-none mt-6', heightClass)}>
          <Doughnut data={chartData} options={options} />
        </div>

        <div className={cn('mt-6 mb-3 flex items-center flex-wrap justify-center mx-auto gap-3 overflow-y-auto custom-scroll', legendMaxWidthClass, legendMaxHeightClass)}>
          {data.map((item, index) => (
            <div key={index} className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded-[3px]"
                style={{ backgroundColor: colors[index] }}
              />
              <span className="text-[#475467] dark:text-textSecondary-dark text-[13px] leading-5 font-medium whitespace-nowrap">
                {item.restaurantName} ({item.percentage.toFixed(1)}%)
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const maximizeButton = (
    <button
      type="button"
      onClick={() => setMaximized(true)}
      aria-label={t('dashboard.maximize')}
      className={cn('p-1.5 rounded-lg hover:bg-black/5 dark:hover:bg-white/10 transition', tertiaryText)}
    >
      <Maximize2 className="w-4 h-4" />
    </button>
  );

  return (
    <>
      <CardWithHeader
        title={title}
        headerActions={toggle}
        headerActionsRight={maximizeButton}
      >
        {renderBody('h-[200px] sm:h-[240px]', 'max-w-[424px]', 'max-h-[180px]')}
      </CardWithHeader>

      <ExpandModal
        isOpen={maximized}
        onClose={() => setMaximized(false)}
        title={title}
        maxWidth="max-w-2xl"
      >
        <div className="flex flex-col">
          <div className="flex justify-end mb-2">{toggle}</div>
          {renderBody('h-[360px] sm:h-[440px]', 'max-w-full', 'max-h-[260px]')}
        </div>
      </ExpandModal>
    </>
  );
};
